import {
  BaseApplicant as Applicant,
  ESessionStorageKeys,
  Loader,
  Merchant,
  StepperFormFooter,
  useMerchantById,
} from '@applyonline/shared'
import { EApplicantType, EFinancingProgram } from '@if/core/types'
import { UpdateCreditApplicationDto, useUpdateCreditApplication } from '../../api/credit-api'
import { getRecaptchaToken } from '../../services/recaptcha'

import { scrollToTop } from '@applyonline/shared/utils'
import { useCallback, useMemo, useState } from 'react'

import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import {
  CreditApplication,
  HomeInformationInput,
  PersonalInformationInput,
} from '../../types/Schemas/CreditApplicationSchema'
import AddressForm from './components/AddressForm'
import PersonalInformationForm from './components/PersonalInformationForm'
import { buildDefaultCreditApplication, useSessionStorage } from './submit-app-hooks'

const getDefaultPrequalInfo = (merchant: Merchant | null, urlMerchantId: string): CreditApplication => {
  return merchant
    ? buildDefaultCreditApplication(merchant)
    : buildDefaultCreditApplication({ id: urlMerchantId } as Merchant)
}

const UpdatePersonalInformationForm = () => {
  const { t } = useTranslation()
  const urlParams = useParams()
  const currentStep = urlParams.currentStep!
  const urlMerchantId = urlParams.merchantId!
  const { id, applicantType } = useParams<{ id: string; applicantType: EApplicantType }>()
  const [merchant, isFetchingMerchant] = useMerchantById(urlMerchantId, EFinancingProgram.MechanicalWarranty)

  const [creditInfo, setCreditInfo] = useSessionStorage<CreditApplication>(
    ESessionStorageKeys.CREDIT_APPLICATION_INFO,
    getDefaultPrequalInfo(merchant, urlMerchantId),
  )
  const [updatePrequal, isUpdating] = useUpdateCreditApplication()
  const { executeRecaptcha } = useGoogleReCaptcha()
  const navigate = useNavigate()
  const [disableNext, setDisableNext] = useState(false)

  const steps = useMemo(() => ['personal-information', 'address'], [])

  const cls = `form-section step-${currentStep}-active`

  const applicationURL = `/creditapplication/${id}`
  const updateApplicantInfoURL = `/${urlMerchantId}/creditapplication/${id}/editPersonalInformation/${applicantType}`

  const gotoStep = useCallback((stepPath: string) => {
    scrollToTop()
    navigate(`${updateApplicantInfoURL}/${stepPath}`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const nextStepAfter = useCallback(() => {
    const nextStepIdx = steps.indexOf(currentStep.toLowerCase()) + 1
    if (nextStepIdx < steps.length) {
      gotoStep(steps[nextStepIdx])
    }
  }, [currentStep, gotoStep, steps])

  const handleCreditApplicationChanged = (data: PersonalInformationInput) => {
    const { applicant, ...otherData } = data
    const updatedCreditInfo = {
      ...creditInfo,
      ...otherData,
      applicant: { ...creditInfo.applicant, ...applicant },
    } as CreditApplication
    setCreditInfo(updatedCreditInfo)
  }

  const handleApplicantChanged = (data: Partial<Applicant>) => {
    const update = { ...creditInfo, applicant: { ...creditInfo.applicant, ...data } }
    setCreditInfo(update)
  }

  const handleSubmitUpdate = async () => {
    const recaptchaToken = await getRecaptchaToken(executeRecaptcha, 'submit_personalInformation')
    const dto: UpdateCreditApplicationDto = {
      creditApplicationId: urlParams.id!,
      personalInfoDto: {
        ...creditInfo,
      },
      recaptchaToken,
    }
    await updatePrequal(dto)
    navigate(`/creditapplication/${urlParams.id}`, { replace: true })
  }

  const handleAddressUpdated = (data: HomeInformationInput) => {
    const applicantData: Partial<Applicant> = {
      currentAddress: data.currentAddress,
      spending: data.spending || undefined,
    }
    handleApplicantChanged(applicantData)
  }

  if (!urlMerchantId || isFetchingMerchant || isUpdating || !merchant)
    return <Loader title={t('common.withYouShortly')} description={t('common.pleaseWait')} />

  return (
    <main className={cls}>
      <h1 className="heading-primary">{t('identity.updateInfo')}</h1>

      {currentStep === 'personal-information' && (
        <PersonalInformationForm
          isCoapplicant={applicantType === EApplicantType.CoApplicant}
          creditApplicationData={creditInfo}
          onCreditApplicationChanged={handleCreditApplicationChanged}
          nextStepAfter={nextStepAfter}
          currentStep={currentStep}
        />
      )}

      {currentStep === 'address' && (
        <AddressForm
          applicantData={creditInfo.applicant}
          nextStepAfter={handleSubmitUpdate}
          currentStep={currentStep}
          onApplicantChanged={handleAddressUpdated}
          applicantName={creditInfo.applicant.firstName}
          setDisableNext={setDisableNext}
          isSinglePayment={creditInfo.isSinglePayment}
          applicantBirthDate={creditInfo.applicant.birthDate}
        />
      )}
      <StepperFormFooter
        currentStepIndex={steps.indexOf(currentStep)}
        disableBack={false}
        disableNext={disableNext}
        steps={steps}
        baseURL={steps.indexOf(currentStep) === 0 ? applicationURL : updateApplicantInfoURL}
      />
    </main>
  )
}

export default UpdatePersonalInformationForm
